import { log, logError } from '../services/logger';
import { base64ToUint8Array, uint8ArrayToBase64 } from '../utils/bytes';

declare const Office: typeof globalThis.Office;

export async function waitForOfficeReady(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof Office === 'undefined') {
      reject(
        new Error(
          'Office.js ist nicht geladen. Bitte die App in Outlook als Add-in öffnen (nicht nur im Browser).',
        ),
      );
      return;
    }

    Office.onReady((info) => {
      log('Office.js initialisiert', info);
      if (info.host === Office.HostType.Outlook) {
        log('Outlook Context erkannt');
        resolve();
        return;
      }
      reject(
        new Error(
          `Falscher Host: ${String(info.host)}. Diese App muss in Outlook geöffnet werden.`,
        ),
      );
    });
  });
}

export function getMailboxUserHint(): { displayName?: string; email?: string } {
  try {
    const user = Office.context?.mailbox?.userProfile;
    if (!user) return {};
    return {
      displayName: user.displayName || undefined,
      email: user.emailAddress || undefined,
    };
  } catch (e) {
    logError('Mailbox userProfile nicht lesbar', e);
    return {};
  }
}

export type MailAttachment = {
  id: string;
  name: string;
  size: number;
  attachmentType: string;
  isInline: boolean;
  contentType: string;
};

export type PdfAttachment = MailAttachment & { isPdf: true };
export type DocxAttachment = MailAttachment & { isDocx: true };
export type SignableAttachment = (PdfAttachment | DocxAttachment) & {
  kind: 'pdf' | 'docx';
};

function isPdfAttachment(att: MailAttachment): boolean {
  const name = (att.name || '').toLowerCase();
  const ct = (att.contentType || '').toLowerCase();
  return name.endsWith('.pdf') || ct === 'application/pdf' || ct.includes('pdf');
}

function isDocxAttachment(att: MailAttachment): boolean {
  const name = (att.name || '').toLowerCase();
  const ct = (att.contentType || '').toLowerCase();
  return (
    name.endsWith('.docx') ||
    ct.includes('wordprocessingml.document') ||
    ct === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  );
}

function mapAttachmentDetails(raw: {
  id: string;
  name?: string;
  size?: number;
  attachmentType?: Office.MailboxEnums.AttachmentType | string;
  isInline?: boolean;
  contentType?: string;
}): MailAttachment {
  return {
    id: raw.id,
    name: raw.name || 'Anhang',
    size: typeof raw.size === 'number' ? raw.size : 0,
    attachmentType: String(raw.attachmentType ?? ''),
    isInline: Boolean(raw.isInline),
    contentType: raw.contentType || '',
  };
}

/**
 * Lists non-inline attachments on the current Outlook item (read or compose).
 * PDF filtering is done by the caller / listPdfAttachments.
 */
export async function listMailAttachments(): Promise<MailAttachment[]> {
  const item = Office.context?.mailbox?.item;
  if (!item) {
    log('Kein mailbox.item – keine Anhänge');
    return [];
  }

  // Read mode: synchronous attachments collection is available on most hosts.
  const syncList = (item as Office.MessageRead).attachments;
  if (Array.isArray(syncList)) {
    try {
      const mapped = syncList.map(mapAttachmentDetails).filter((a) => !a.isInline);
      log('Anhänge (sync)', { count: mapped.length });
      return mapped;
    } catch (e) {
      logError('Sync-Anhänge nicht lesbar', e);
    }
  }

  // Compose / fallback: getAttachmentsAsync when available
  const compose = item as Office.MessageCompose;
  if (typeof compose.getAttachmentsAsync === 'function') {
    return new Promise((resolve) => {
      compose.getAttachmentsAsync((result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          logError('getAttachmentsAsync fehlgeschlagen', result.error);
          resolve([]);
          return;
        }
        const mapped = (result.value || [])
          .map(mapAttachmentDetails)
          .filter((a) => !a.isInline);
        log('Anhänge (async)', { count: mapped.length });
        resolve(mapped);
      });
    });
  }

  return [];
}

export async function listPdfAttachments(): Promise<PdfAttachment[]> {
  const all = await listMailAttachments();
  return all.filter(isPdfAttachment).map((a) => ({ ...a, isPdf: true as const }));
}

export async function listSignableAttachments(): Promise<SignableAttachment[]> {
  const all = await listMailAttachments();
  const out: SignableAttachment[] = [];
  for (const a of all) {
    if (isPdfAttachment(a)) {
      out.push({ ...a, isPdf: true, kind: 'pdf' });
    } else if (isDocxAttachment(a)) {
      out.push({ ...a, isDocx: true, kind: 'docx' });
    }
  }
  return out;
}

export type AttachmentLoadResult = {
  id: string;
  name: string;
  format: string;
  /** Content length only — never log or persist the payload. */
  byteLength: number;
};

/**
 * Loads attachment content via Office.js to verify it can be read (Phase 2 checkpoint).
 * Does not write to disk or our server.
 */
export async function loadAttachmentContent(
  attachmentId: string,
  name: string,
): Promise<AttachmentLoadResult> {
  const bytes = await getAttachmentBytes(attachmentId, name);
  return {
    id: attachmentId,
    name,
    format: 'Base64',
    byteLength: bytes.byteLength,
  };
}

/**
 * Loads full attachment bytes via Office.js (Base64 file attachments).
 * Does not upload to our server.
 */
export async function getAttachmentBytes(
  attachmentId: string,
  name: string,
): Promise<Uint8Array> {
  const item = Office.context?.mailbox?.item;
  if (!item || typeof item.getAttachmentContentAsync !== 'function') {
    throw new Error('Anhänge können in diesem Outlook-Kontext nicht geladen werden.');
  }

  return new Promise((resolve, reject) => {
    item.getAttachmentContentAsync(attachmentId, (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        const msg = result.error?.message || 'getAttachmentContentAsync fehlgeschlagen';
        reject(new Error(msg));
        return;
      }
      const value = result.value;
      const format = String(value?.format ?? '');
      const content = value?.content ?? '';

      if (/base64/i.test(format) || (!format && content.length > 0)) {
        try {
          const bytes = base64ToUint8Array(content);
          log('Anhang-Bytes geladen', { name, bytes: bytes.byteLength, format });
          resolve(bytes);
          return;
        } catch (e) {
          reject(e instanceof Error ? e : new Error(String(e)));
          return;
        }
      }

      if (/url/i.test(format)) {
        reject(
          new Error(
            'Dieser Anhang liegt nur als URL vor und kann hier nicht geladen werden. Bitte die Datei lokal speichern und erneut anhängen.',
          ),
        );
        return;
      }

      reject(new Error(`Anhang-Format wird nicht unterstützt: ${format || 'unbekannt'}`));
    });
  });
}

/** True when the open item is a draft / compose surface (not read). */
export function isComposeMode(): boolean {
  try {
    const item = Office.context?.mailbox?.item as
      | (Office.MessageCompose & { displayReplyForm?: unknown })
      | undefined;
    if (!item) return false;
    // Read items expose displayReplyForm; compose does not.
    if (typeof item.displayReplyForm === 'function') return false;
    return typeof item.addFileAttachmentFromBase64Async === 'function';
  } catch {
    return false;
  }
}

/**
 * Attach signed file in compose mode when supported.
 * Returns attachment id on success, null otherwise (never throws).
 */
export async function tryAddFileAttachmentFromBytes(
  fileName: string,
  bytes: Uint8Array,
): Promise<string | null> {
  const item = Office.context?.mailbox?.item as Office.MessageCompose | undefined;
  if (!item || typeof item.addFileAttachmentFromBase64Async !== 'function') {
    return null;
  }
  // Avoid calling reply APIs on compose; attach only makes sense here.
  if (typeof (item as { displayReplyForm?: unknown }).displayReplyForm === 'function') {
    return null;
  }

  let b64: string;
  try {
    b64 = uint8ArrayToBase64(bytes);
  } catch (e) {
    logError('Base64 für Anhang fehlgeschlagen', e);
    return null;
  }

  return new Promise((resolve) => {
    try {
      item.addFileAttachmentFromBase64Async(b64, fileName, { isInline: false }, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          const id = typeof result.value === 'string' ? result.value : '';
          log('Signiertes Dokument als Anhang hinzugefügt', { fileName, id: id || '(kein id)' });
          resolve(id || 'ok');
          return;
        }
        logError('addFileAttachmentFromBase64Async fehlgeschlagen', result.error);
        resolve(null);
      });
    } catch (e) {
      // z.B. ReadItem statt ReadWriteItem → synchrone „erhöhte Rechte“-Exception
      logError('addFileAttachmentFromBase64Async Exception', e);
      resolve(null);
    }
  });
}

/** Remove a compose attachment by id. Returns false on failure (never throws). */
export async function tryRemoveAttachment(attachmentId: string): Promise<boolean> {
  const item = Office.context?.mailbox?.item as Office.MessageCompose | undefined;
  if (!item || !attachmentId || typeof item.removeAttachmentAsync !== 'function') {
    return false;
  }

  return new Promise((resolve) => {
    try {
      item.removeAttachmentAsync(attachmentId, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          log('Anhang entfernt', { attachmentId });
          resolve(true);
          return;
        }
        logError('removeAttachmentAsync fehlgeschlagen', result.error);
        resolve(false);
      });
    } catch (e) {
      logError('removeAttachmentAsync Exception', e);
      resolve(false);
    }
  });
}

export type ReplaceComposeAttachmentResult = {
  attached: boolean;
  removedOriginal: boolean;
  detail?: string;
};

/**
 * Compose: signierte Datei anhängen und (danach) das unsignierte Original entfernen.
 */
export async function replaceComposeAttachmentWithSigned(opts: {
  originalAttachmentId: string;
  signedFileName: string;
  signedBytes: Uint8Array;
}): Promise<ReplaceComposeAttachmentResult> {
  const attachedId = await tryAddFileAttachmentFromBytes(opts.signedFileName, opts.signedBytes);
  if (!attachedId) {
    return {
      attached: false,
      removedOriginal: false,
      detail: 'Konnte signierte Datei nicht anhängen.',
    };
  }

  const removedOriginal = await tryRemoveAttachment(opts.originalAttachmentId);
  if (!removedOriginal) {
    return {
      attached: true,
      removedOriginal: false,
      detail:
        'Signierte Datei angehängt, Original konnte nicht automatisch entfernt werden — bitte manuell löschen.',
    };
  }

  return { attached: true, removedOriginal: true };
}

export type OpenReplyResult = {
  opened: boolean;
  /** True if reply form was opened with the PDF attached (Mailbox 1.15+). */
  withAttachment: boolean;
  detail?: string;
};

/**
 * Opens a reply to the current message with the signed PDF attached (Base64).
 * Requires Mailbox 1.15+. Falls back to reply without attachment if Base64 fails.
 */
export async function openReplyWithSignedPdf(
  fileName: string,
  pdfBytes: Uint8Array,
): Promise<OpenReplyResult> {
  const item = Office.context?.mailbox?.item as Office.MessageRead | undefined;
  if (!item) {
    return { opened: false, withAttachment: false, detail: 'Kein Mail-Kontext.' };
  }

  const safeName = fileName.replace(/[<>&"']/g, '') || 'Dokument-signiert.pdf';
  const htmlBody =
    `<p>Guten Tag,</p>` +
    `<p>anbei erhalten Sie das signierte Dokument <strong>${safeName}</strong>.</p>` +
    `<p>Mit freundlichen Grüßen</p>`;

  const b64 = uint8ArrayToBase64(pdfBytes);
  const formDataWithAtt: Office.ReplyFormData = {
    htmlBody,
    attachments: [
      {
        type: Office.MailboxEnums.AttachmentType.Base64,
        name: safeName,
        base64file: b64,
        inLine: false,
      },
    ],
  };

  const tryAsync = (
    method: 'displayReplyFormAsync' | 'displayReplyAllFormAsync',
    data: Office.ReplyFormData,
  ): Promise<boolean> =>
    new Promise((resolve) => {
      const fn = item[method];
      if (typeof fn !== 'function') {
        resolve(false);
        return;
      }
      fn.call(item, data, (result: Office.AsyncResult<void>) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(true);
          return;
        }
        logError(`${method} fehlgeschlagen`, result.error);
        resolve(false);
      });
    });

  // Prefer reply-to-sender with attachment
  if (await tryAsync('displayReplyFormAsync', formDataWithAtt)) {
    log('Antwort mit signiertem PDF geöffnet');
    return { opened: true, withAttachment: true };
  }

  // Sync fallback
  if (typeof item.displayReplyForm === 'function') {
    try {
      item.displayReplyForm(formDataWithAtt);
      log('Antwort mit signiertem PDF geöffnet (sync)');
      return { opened: true, withAttachment: true };
    } catch (e) {
      logError('displayReplyForm mit Anhang fehlgeschlagen', e);
    }
  }

  // Reply without attachment (older hosts)
  const plain: Office.ReplyFormData = { htmlBody };
  if (await tryAsync('displayReplyFormAsync', plain)) {
    return {
      opened: true,
      withAttachment: false,
      detail: 'Antwort geöffnet, Anhang konnte nicht automatisch eingefügt werden (Outlook-Version).',
    };
  }
  if (typeof item.displayReplyForm === 'function') {
    try {
      item.displayReplyForm(htmlBody);
      return {
        opened: true,
        withAttachment: false,
        detail: 'Antwort geöffnet, Anhang konnte nicht automatisch eingefügt werden.',
      };
    } catch (e) {
      logError('displayReplyForm fehlgeschlagen', e);
    }
  }

  return {
    opened: false,
    withAttachment: false,
    detail: 'Antwortfenster konnte nicht geöffnet werden.',
  };
}
